import { addResponseStatusToSpan } from '@vs-shop/engine/utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import type { Paths } from '@vs-shop/schemas/openapi/paths';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '@vs-shop/engine/service/logger/ApiModule';
import { logApiOperation } from '@vs-shop/engine/utils/logger/apiLogger';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/banner';
const HTTP_METHOD = 'get';

export type GetBannersQuery = Paths[typeof ENDPOINT][typeof HTTP_METHOD] extends {
  parameters: { query?: infer Query };
}
  ? Query
  : never;

export type MakeGetBannersDeps<
  BannerSchema extends Schema.Top,
  BannerPlacementInput extends string,
> = {
  readonly banner: BannerSchema;
  readonly bannerPlacementQuery: (placement: BannerPlacementInput) => GetBannersQuery;
};

export const makeGetBanners = <
  BannerSchema extends Schema.Top,
  BannerPlacementInput extends string,
>(
  deps: MakeGetBannersDeps<BannerSchema, BannerPlacementInput>
) => {
  const ListBannersError = createBackendApiError({
    domain: 'Banner',
    endpoint: 'List',
    path: ENDPOINT,
    identifierField: 'placement',
  });

  const listBannersFetch = (placement?: BannerPlacementInput) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({
          query: placement !== undefined ? deps.bannerPlacementQuery(placement) : {},
        });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          ...(placement !== undefined ? { 'banners.placement': placement } : {}),
        },
      })
    );

  const listBanners = (placement?: BannerPlacementInput) =>
    listBannersFetch(placement).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(HydraCollectionResponse(deps.banner))(response),
          404: (response) =>
            Effect.fail(new ListBannersError.NotFoundError({ placement, response })),
          orElse: (response) =>
            Effect.fail(new ListBannersError.ApiRequestError({ placement, response })),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.BannerList,
          'Debug',
          `Successfully resolved ${String(result['hydra:member'].length)} banners${placement !== undefined ? `, placement ${placement}` : ''}`
        )
      ),
      Effect.withSpan('api.banner.list', {
        kind: 'internal',
        attributes: placement !== undefined ? { 'banners.placement': placement } : {},
      })
    );

  return { ListBannersError, listBanners };
};
